import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

export interface ExternalSystemOption {
  id: string;
  name: string;
}

interface ExternalSystemEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  externalSystem: string | null;
  externalSystemRecordId: string | null;
  availableSystems: ExternalSystemOption[];
  onSave: (system: string | null, recordId: string | null) => Promise<void>;
}

export default function ExternalSystemEditDialog({
  open,
  onOpenChange,
  externalSystem,
  externalSystemRecordId,
  availableSystems,
  onSave,
}: ExternalSystemEditDialogProps) {
  const [selectedSystem, setSelectedSystem] = useState<string | null>(externalSystem);
  const [recordId, setRecordId] = useState(externalSystemRecordId || '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Reset form state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedSystem(externalSystem);
      setRecordId(externalSystemRecordId || '');
    }
  }, [open, externalSystem, externalSystemRecordId]);

  const handleSystemChange = (value: string) => {
    if (value === '__none__') {
      setSelectedSystem(null);
      setRecordId('');
    } else {
      setSelectedSystem(value);
    }
  };

  const handleSaveClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      await onSave(selectedSystem, recordId.trim() || null);
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent via toast
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedSystem(externalSystem);
    setRecordId(externalSystemRecordId || '');
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit External System Link</DialogTitle>
          </DialogHeader>

          {/* Warning Alert */}
          <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Changing these values may break the data lineage between Prolarus and your external system. 
              Only modify if you understand the implications.
            </AlertDescription>
          </Alert>

          {/* Form Fields */}
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="external-system">External System</Label>
              <Select
                value={selectedSystem || '__none__'}
                onValueChange={handleSystemChange}
              >
                <SelectTrigger id="external-system">
                  <SelectValue placeholder="Select a system" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {availableSystems.map((system) => (
                    <SelectItem key={system.id} value={system.name}>
                      {system.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="record-id">External Record ID</Label>
              <Input
                id="record-id"
                value={recordId}
                onChange={(e) => setRecordId(e.target.value)}
                placeholder="Enter the record ID from the external system"
                className="font-mono"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSaveClick}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to update the external system link? This may affect data 
              synchronization with the source system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
